import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function DirectionalDrilling() {
  const [survey, setSurvey] = useState({ i1: "30", a1: "45", i2: "33", a2: "50", cl: "95" });
  const [dlsOut, setDlsOut] = useState(null);
  const [mcOut, setMcOut] = useState(null);

  const parseN = () => Object.fromEntries(Object.entries(survey).map(([k, v]) => [k, parseFloat(v)]));

  const runDls = async () => {
    try {
      const r = await runCalc("dogleg_severity", parseN());
      setDlsOut(r);
    } catch {
      toast.error("Invalid inputs");
    }
  };
  const runMc = async () => {
    try {
      const r = await runCalc("min_curvature", parseN());
      setMcOut(r);
    } catch {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="directional-page">
      <Section
        title="Directional Drilling"
        subtitle="Dogleg severity and Minimum Curvature Method for well path planning. Enter two adjacent survey stations (inclination, azimuth) and course length between them."
      >
        <div className="border border-[#334155] bg-[#11151c] p-5 mb-5">
          <h3 className="heading text-sm font-bold uppercase tracking-wide mb-4">Survey Stations</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <NumField testid="dir-i1" label="Inclination 1" unit="°" value={survey.i1} onChange={(v) => setSurvey({ ...survey, i1: v })} />
            <NumField testid="dir-a1" label="Azimuth 1" unit="°" value={survey.a1} onChange={(v) => setSurvey({ ...survey, a1: v })} />
            <NumField testid="dir-i2" label="Inclination 2" unit="°" value={survey.i2} onChange={(v) => setSurvey({ ...survey, i2: v })} />
            <NumField testid="dir-a2" label="Azimuth 2" unit="°" value={survey.a2} onChange={(v) => setSurvey({ ...survey, a2: v })} />
            <NumField testid="dir-cl" label="Course Length" unit="ft" value={survey.cl} onChange={(v) => setSurvey({ ...survey, cl: v })} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="dls-card" title="Dogleg Severity" formula="DLS = β·100/CL">
            <div className="flex justify-between items-center">
              <PrimaryButton testid="dls-run" onClick={runDls}>Calculate DLS</PrimaryButton>
              <SaveButton getPayload={() => dlsOut && { category: "Directional", calculator: "dogleg_severity", inputs: survey, outputs: dlsOut, units: "field", default_name: "DLS" }} />
            </div>
            {dlsOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Dogleg angle β" value={dlsOut.dogleg_deg} unit="°" />
                <ResultRow label="Dogleg Severity" value={dlsOut.dls_deg_per_100ft} unit="°/100ft" emphasis />
                {dlsOut.dls_deg_per_100ft > 6 && (
                  <div className="text-[11px] mono text-[#fbbf24] mt-2 border-l-2 border-[#f59e0b] pl-2">
                    DLS &gt; 6°/100ft — high dogleg. Review directional plan, torque & drag.
                  </div>
                )}
              </div>
            )}
          </CalcCard>

          <CalcCard testid="mc-card" title="Minimum Curvature Method" formula="ΔTVD, ΔN, ΔE from RF">
            <div className="flex justify-between items-center">
              <PrimaryButton testid="mc-run" onClick={runMc}>Compute Δ</PrimaryButton>
              <SaveButton getPayload={() => mcOut && { category: "Directional", calculator: "min_curvature", inputs: survey, outputs: mcOut, units: "field", default_name: "Min Curvature" }} />
            </div>
            {mcOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Ratio factor RF" value={mcOut.ratio_factor} unit="—" />
                <ResultRow label="ΔTVD" value={mcOut.delta_tvd_ft} unit="ft" emphasis />
                <ResultRow label="ΔNorth" value={mcOut.delta_north_ft} unit="ft" />
                <ResultRow label="ΔEast" value={mcOut.delta_east_ft} unit="ft" />
                <ResultRow label="DLS" value={mcOut.dls_deg_per_100ft} unit="°/100ft" />
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
